/**
 * "Aufgaben" — globale Aufgabenliste auf der Startseite.
 *
 * Datenquelle: GET /v1/admin/tasks (letzte 3 Monate, offene Aufgaben).
 * Schalter "Nur meine / Alle" (CRM 1.0: Checkbox "Nur meine Aufgaben
 * anzeigen", Default an) → filters[showMyTasks]. Die Wahl wird pro Admin im
 * localStorage gemerkt. Admin-Rollen 3–6 bekommen serverseitig immer nur die
 * eigenen Aufgaben — für sie ist der Schalter fest auf "Nur meine" und
 * deaktiviert.
 *
 * Aktionen wie im PB-AufgabenWidget: Kreis-Button = erledigt/wieder öffnen,
 * Klick auf die Zeile = Bearbeiten im Drawer (TaskEditForm), Name der
 * betroffenen Person = PB- bzw. BK-Detail.
 */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Badge } from '../../../../components/ui';
import { EditDrawer } from '../../../../components/ui/EditDrawer';
import { BRAND, getThemeColors } from '../../../../lib/theme';
import { AppLink } from '../../../../lib/openLink';
import { usePermissions } from '../../../../contexts/PermissionsContext';
import { v2Dashboard, v1Tasks, type LaravelPagination, type V1DashboardTask } from '../../../../lib/v1';
import { Switch } from '../../../pflege/profile/controls';
import { TaskEditForm } from '../../../pflege/PbDashboard/AufgabenWidget';
import { assigneeNames, fmtDt, useActiveAdmins } from '../../../pflege/PbDashboard/helpers';
import type { TaskRow } from '../../../pflege/PbDashboard/types';
import { TaskPriorityBadge, highPriorityRowStyle, isHighPriority } from '../../../pflege/PbDashboard/TaskPriority';
import { WidgetPager, isBool, isPerPage, readWidgetPref, writeWidgetPref } from './widgetTable';

/** Admin-Rollen, die serverseitig nur eigene Aufgaben sehen (UserController::getTasks). */
export const OWN_TASKS_ONLY_ROLES = [3, 4, 5, 6];

export function isOwnTasksOnlyRole(roleId: number | null | undefined): boolean {
  return roleId != null && OWN_TASKS_ONLY_ROLES.includes(roleId);
}

const STATUS_TONE: Record<number, 'info' | 'success' | 'warning'> = { 0: 'info', 1: 'success', 2: 'warning' };
const STATUS_KEY: Record<number, string> = { 0: 'open', 1: 'done', 2: 'in_progress' };

function isOverdue(task: V1DashboardTask, now = Date.now()): boolean {
  if (task.status === 1 || !task.zieldatum) return false;
  const d = new Date(String(task.zieldatum).replace(' ', 'T'));
  return !isNaN(d.getTime()) && d.getTime() < now;
}

interface TasksWidgetProps {
  mode: 'full' | 'summary';
  dark: boolean;
  tr: (k: string, opts?: Record<string, unknown>) => string;
  onTotal?: (total: number | null) => void;
}

const SUMMARY_ROWS = 5;

export function TasksWidget({ mode, dark, tr, onTotal }: TasksWidgetProps) {
  const t = getThemeColors(dark);
  const { role } = usePermissions();
  const forcedOwn = isOwnTasksOnlyRole(role?.id);
  const admins = useActiveAdmins();

  const [onlyMineChoice, setOnlyMineChoice] = useState<boolean>(() => readWidgetPref('tasks.onlyMine', true, isBool));
  const onlyMine = forcedOwn || onlyMineChoice;
  const [perPage, setPerPage] = useState<number>(() => readWidgetPref('tasks.perPage', 10, isPerPage));
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<LaravelPagination<V1DashboardTask> | null>(null);
  // Ladezustand abgeleitet statt per setState im Effect: lädt, solange die
  // zuletzt beantwortete Anfrage nicht der aktuellen entspricht.
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [editing, setEditing] = useState<V1DashboardTask | null>(null);

  const effPerPage = mode === 'summary' ? SUMMARY_ROWS : perPage;

  const requestKey = JSON.stringify([page, effPerPage, onlyMine, reloadTick]);
  const loading = loadedKey !== requestKey;

  useEffect(() => {
    let alive = true;
    // Nach Fälligkeit sortiert (ohne Fälligkeit zuletzt), siehe v2Dashboard.tasks.
    v2Dashboard.tasks({
      page,
      perPage: effPerPage,
      filters: { showFinishedTasks: 'false', showMyTasks: onlyMine ? 'true' : 'false' },
    })
      .then(p => {
        if (!alive) return;
        setResult(p);
        setError(false);
        onTotal?.(p.total);
        if (p.last_page > 0 && p.current_page > p.last_page) setPage(p.last_page);
      })
      .catch(() => { if (alive) { setError(true); onTotal?.(null); } })
      .finally(() => { if (alive) setLoadedKey(requestKey); });
    return () => { alive = false; };
  }, [page, effPerPage, onlyMine, reloadTick, requestKey, onTotal]);

  const changeOnlyMine = (v: boolean) => {
    setOnlyMineChoice(v);
    writeWidgetPref('tasks.onlyMine', v);
    setPage(1);
  };

  const changePerPage = (n: number) => {
    setPerPage(n);
    writeWidgetPref('tasks.perPage', n);
    setPage(1);
  };

  const toggleDone = async (task: V1DashboardTask, e: React.MouseEvent) => {
    e.stopPropagation();
    const newStatus = task.status === 1 ? 0 : 1;
    const patch = (status: number | undefined) => setResult(prev => prev
      ? { ...prev, data: prev.data.map(x => (x.id === task.id ? { ...x, status } : x)) }
      : prev);
    patch(newStatus);
    try {
      await v1Tasks.update(task.id, {
        titel: task.titel ?? tr('pflege.tasks.no_title'),
        beschreibung: task.beschreibung ?? '',
        status: newStatus,
      });
    } catch {
      patch(task.status);
    }
  };

  const switchRow = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11.5, color: t.muted }}>
      <span style={{ fontWeight: onlyMine ? 400 : 600, color: onlyMine ? t.muted : t.heading }}>
        {tr('dashboard.widget.tasks.filter_all')}
      </span>
      <span
        title={forcedOwn ? tr('dashboard.widget.tasks.forced_own_hint') : undefined}
        style={{ display: 'inline-flex', opacity: forcedOwn ? 0.5 : 1, pointerEvents: forcedOwn ? 'none' : undefined }}
        aria-disabled={forcedOwn || undefined}
      >
        <Switch
          dark={dark}
          value={onlyMine}
          onChange={forcedOwn ? () => { /* Rolle sieht nur eigene Aufgaben */ } : changeOnlyMine}
          ariaLabel={tr('dashboard.widget.tasks.only_mine_label')}
        />
      </span>
      <span style={{ fontWeight: onlyMine ? 600 : 400, color: onlyMine ? t.heading : t.muted }}>
        {tr('dashboard.widget.tasks.filter_mine')}
      </span>
      {forcedOwn && (
        <span style={{ fontSize: 10.5, color: t.muted, fontStyle: 'italic' }}>
          {tr('dashboard.widget.tasks.forced_own_hint')}
        </span>
      )}
    </div>
  );

  const rows = result?.data ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <div style={{ paddingBottom: 8, borderBottom: `1px solid ${t.borderSoft}` }}>{switchRow}</div>

      {!result ? (
        <div style={{ fontSize: 12, color: t.muted, paddingTop: 10 }}>
          {error ? tr('dashboard.widget.load_error') : tr('dashboard.widget.loading')}
        </div>
      ) : (
        <div style={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.15s' }}>
          {rows.length === 0 && (
            <div style={{ padding: '12px 0', fontSize: 12, color: t.muted }}>{tr('dashboard.widget.tasks.empty')}</div>
          )}
          {rows.map((task, i) => {
            const done = task.status === 1;
            const status = typeof task.status === 'number' ? task.status : 0;
            const title = task.titel || tr('pflege.tasks.no_title');
            const names = assigneeNames(task.bearbeiter ?? null, admins);
            const assignees = [names, task.gruppen].filter(Boolean).join(' · ');
            const overdue = isOverdue(task);
            const person = (task.user_name ?? '').trim()
              || (task.user_id != null ? tr('dashboard.widget.tasks.person_fallback', { id: task.user_id }) : '');
            return (
              <div
                key={task.id}
                role="button"
                tabIndex={0}
                aria-label={tr('dashboard.widget.tasks.edit_aria', { title })}
                onClick={() => setEditing(task)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditing(task); } }}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10, padding: '9px 0',
                  borderBottom: i < rows.length - 1 ? `1px solid ${t.borderSoft}` : 'none',
                  opacity: done ? 0.55 : 1, cursor: 'pointer',
                  ...highPriorityRowStyle(task, dark),
                  // Abstand zum roten Rand (die Zeile hat sonst kein Seiten-Padding).
                  ...(isHighPriority(task) && !done ? { paddingLeft: 10, paddingRight: 6 } : {}),
                }}
              >
                <button
                  type="button"
                  title={done ? tr('pflege.detail.dashboard.task_reopen') : tr('pflege.detail.dashboard.task_done')}
                  aria-label={done ? tr('pflege.detail.dashboard.task_reopen') : tr('pflege.detail.dashboard.task_done')}
                  onClick={e => toggleDone(task, e)}
                  style={{
                    flexShrink: 0, width: 20, height: 20, borderRadius: '50%', marginTop: 1,
                    border: `1.5px solid ${done ? '#12b76a' : t.border}`, background: done ? '#12b76a' : 'transparent',
                    color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                  }}
                >{done ? '✓' : ''}</button>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 12.5, fontWeight: 600, color: t.heading, textDecoration: done ? 'line-through' : 'none',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>{title}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '2px 8px', fontSize: 11, color: t.muted, marginTop: 2 }}>
                    {person && (task.user_id != null ? (
                      <AppLink
                        page={task.user_role === 1 ? 'betreuung' : 'pflege'}
                        sub={String(task.user_id)}
                        stopPropagation
                        style={{ textDecoration: 'none', cursor: 'pointer', fontWeight: 500, color: BRAND }}
                      >{person}</AppLink>
                    ) : (
                      <span style={{ fontWeight: 500, color: BRAND }}>{person}</span>
                    ))}
                    <span style={{ color: overdue ? '#d92d20' : t.muted, fontWeight: overdue ? 600 : 400 }}>
                      {task.zieldatum
                        ? tr('dashboard.widget.tasks.due', { date: fmtDt(task.zieldatum) })
                        : tr('dashboard.widget.tasks.no_due')}
                    </span>
                  </div>
                  {assignees && (
                    <div style={{ fontSize: 10.5, color: t.muted, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {tr('pflege.detail.dashboard.task_assignee_prefix')} {assignees}
                    </div>
                  )}
                </div>
                <TaskPriorityBadge task={task} />
                <Badge tone={STATUS_TONE[status] ?? 'neutral'} dark={dark}>
                  {tr(`dashboard.widget.tasks.status.${STATUS_KEY[status] ?? 'open'}`)}
                </Badge>
              </div>
            );
          })}
          {mode === 'full' && (
            <WidgetPager
              dark={dark}
              page={result.current_page}
              lastPage={result.last_page}
              total={result.total}
              perPage={perPage}
              onPage={setPage}
              onPerPage={changePerPage}
              tr={tr}
            />
          )}
        </div>
      )}

      {/* Portal: react-grid-layout positioniert die Karten per CSS-transform —
          ein position:fixed-Drawer würde sonst an der Karte statt am Fenster
          kleben. Nur gemountet, solange bearbeitet wird (EditDrawer sperrt
          beim Mount das Body-Scrolling). */}
      {editing && createPortal(
      <EditDrawer
        open
        onClose={() => setEditing(null)}
        title={tr('dashboard.widget.tasks.edit_title')}
        dark={dark}
      >
        {editing && (
          <TaskEditForm
            dark={dark}
            task={editing as TaskRow}
            userId={editing.user_id ?? 0}
            admins={admins}
            onSaved={() => { setEditing(null); setReloadTick(n => n + 1); }}
            onCancel={() => setEditing(null)}
          />
        )}
      </EditDrawer>,
      document.body,
      )}
    </div>
  );
}
